<template>
  <div
    v-if="loadingGmf"
    class="flex-1 f-center anim-fade-in_slow"
  >
    <LoaderWLogo />
  </div>
  <div
    v-else
    id="solution-browser"
    class="solbrowser text-noselect"
    tour="solutionbrowser"
  >
    <StudyDataLayoutLayer v-if="!$store.getters.user_has_flag('CR_REPORTS')" />
    <ReportsDataLayer
      v-else-if="soltab === 'reports'"
      v-bind="commonProps"
      key="reports"
      :gamestateNamespace
    />
    <StudyDataLayer
      v-else
      key="study"
      v-bind="commonProps"
      :gamestateNamespace
    />

    <portal
      to="main_before"
      :order="1"
    >
      <FloatingGmfSelector
        v-if="!mobile"
        :namespace="gamestateNamespace"
        :mobile
        data-tst="floating_gmf_selector"
      />
      <FloatingBubbleFactor
        v-if="!mobile"
        :namespace="gamestateNamespace"
      />
    </portal>
  </div>
</template>

<script>
import { throttle } from 'lodash'
import { replaceLastCardFromGrid, getCardsFromStr } from '@/helpers/cards'
import { namespaces } from '@/helpers/constants'
import { allSwitchers } from '@/store/switchers/switchers'

import FloatingGmfSelector from '@/components/GameFormat/GmfSelektor/FloatingGmfSelector.vue'
import FloatingBubbleFactor from '@/components/BubbleFactor/FloatingBubbleFactor.vue'
import LoaderWLogo from '@/components/Loaders/LoaderWLogo.vue'

import StudyDataLayoutLayer from '@/components/Study/StudyDataLayoutLayer.vue'
import ReportsDataLayer from '@/components/Study/under_flag/ReportsDataLayer.vue'
import StudyDataLayer from '@/components/Study/under_flag/StudyDataLayer.vue'

import prompt_mix from '@/mixins/prompt_mix'
import { SNK } from '@/helpers/helpers'

export default {
  name: 'StudySolutions',
  components: {
    FloatingGmfSelector,
    FloatingBubbleFactor,
    LoaderWLogo,
    StudyDataLayoutLayer,
    ReportsDataLayer,
    StudyDataLayer,
  },
  mixins: [prompt_mix],
  data() {
    return {
      loadingGmf: true,
    }
  },
  async created() {
    if (this.$store.getters.user_has_flag('CR_REPORTS')) {
      // this is kinda unfortunate but we need to set the soltab here, as we cannot wait for created() hook in TabBoxStudy
      // once we really split Reports and Study to separate pages we can remove this
      const { soltab } = this.$route.query
      if (
        allSwitchers()
          .sw_tabs_study.map(i => i.payload)
          .includes(soltab)
      ) {
        this.$store.dispatch('switchers_set_item', {
          key: 'sw_tabs_study',
          item: { payload: soltab },
        })
      } else {
        this.$store.dispatch('switchers_set_item', {
          key: 'sw_tabs_study',
          item: { payload: allSwitchers().sw_tabs_study[0].payload },
        })
      }
    }

    this.$store.dispatch('globals_footer_tab', 'browse')

    await this.$store.dispatch('gameformats_init_pageViewStores', { forceInit: true })

    if (!this.$store.getters.user_has_flag('CR_REPORTS')) {
      await this.$store.dispatch(
        SNK('sol_studyInitialize', this.gamestateNamespace),
        { actionArgs: { callHistory: false } },
        { root: true },
      )
    }

    // TODO tst this
    // Force auto jump for FREE tier users so that they don't trigger last flop
    // as its the only free flop they have for the day - this way they can change it every day without the limit trigger
    const gameformat = this.$store.getters[SNK('gameformat', this.gamestateNamespace)]
    const query_solution_type =
      this.$store.getters[SNK('query_solution_type', this.gamestateNamespace)]
    if (query_solution_type === 'gwiz' && !gameformat.is_subscribed) {
      const board = this.$store.getters[SNK('query_board', this.gamestateNamespace)]
      const flop_actions =
        this.$store.getters[SNK('query_history', this.gamestateNamespace)].flop_actions
      if (board || flop_actions) {
        this.$store.dispatch(SNK('router_update_query', this.gamestateNamespace), {
          query: {
            history_spot: 0,
          },
          push: false,
          replace: true,
          saveLocally: true,
        })
      }
    }

    // http://localhost:8080/solutions?custree_id=49c3b7ff-7ea1-40a7-996e-8851c838909d&flop_actions=X-R5.5&history_spot=2&solution_type=custom&soltab=strategy&gmfs_solution_tab=ai_sols&board=QsTs7h&autocreate_custom_solution=true
    if (this.$route.query.autocreate_custom_solution === 'true') {
      this.$router.replace({
        query: {
          ...this.$route.query,
          autocreate_custom_solution: undefined,
        },
      })

      const { board, custree_id: custom_tree_id } = this.$route.query
      if (board && custom_tree_id) {
        await this.$store.dispatch(SNK('sol_autoCreateCustomSolution', this.gamestateNamespace), {
          board,
          custom_tree_id,
        })
      }
    }

    this.loadingGmf = false
  },
  methods: {
    getNextBoardCard: throttle(function (move) {
      const hsBoard = this.$store.getters[SNK('history_spot_board', this.gamestateNamespace)]
      let board = getCardsFromStr(this.$store.getters[SNK('query_board', this.gamestateNamespace)])
      board = board.filter(c => !c.includes('E'))
      if (!board.length) return

      let bb = replaceLastCardFromGrid({
        cards: hsBoard,
        skipCards: board,
        move,
      })
      bb = board.map((cc, idx) => bb[idx] || cc)
      board = bb.join('')

      if (this.usingCustomSol) {
        const next_actions = this.$store.getters.api_get(
          'api_poker_next_actions',
          this.namespace,
        )?.loading
        const history = this.$store.getters.api_get('api_poker_history', this.namespace)?.loading
        const cussol = this.$store.getters.api_get(
          'api_poker_custom_solution',
          this.namespace,
        )?.loading
        if (next_actions || history || cussol) return

        this.$store.dispatch(SNK('router_update_query', this.gamestateNamespace), {
          query: { board },
          push: true,
        })
        const solElite = this.$store.getters[SNK('sol_elite', this.gamestateNamespace)]
        this.$store.dispatch(SNK('sol_cust_handleBoardChange', this.gamestateNamespace), {
          board,
          solElite,
        })
        return
      }
      this.$store.dispatch(SNK('router_update_query', this.gamestateNamespace), {
        query: { board },
        push: true,
      })
    }, 222),
    getNextGameformat: throttle(function (num) {
      const solElite = this.$store.getters[SNK('sol_elite', this.gamestateNamespace)]
      if (solElite.want_use) {
        // same as in SavedSolutionsTableV1

        const dashVersion =
          {
            v1: 'v1',
            v2: 'v2',
          }[this.$store.getters.solfigrDashboard] || 'v2'

        const tcrud_apiData =
          dashVersion === 'v1'
            ? this.$store.getters.api_get_data('api_poker_saved_solutions_table')
            : this.$store.getters.api_get_data('api_poker_custom_solutions_table')
        let tableRows = tcrud_apiData?.results || []
        if (dashVersion === 'v1') {
          tableRows = tableRows.filter(i => i.is_favorite) // to hide after unfavoriting
        }
        if (!tableRows.length) return

        const cussolId = this.$store.getters[SNK('query_cussol_id', this.gamestateNamespace)]
        let activeIdx = tableRows.findIndex(i => i.id === cussolId)
        if (activeIdx === -1) activeIdx = 0
        let nextIdx = activeIdx + num
        const l = tableRows.length - 1
        nextIdx = nextIdx > l ? 0 : nextIdx < 0 ? l : nextIdx
        const item = tableRows[nextIdx]

        const channelId = this.$store.getters.bc_channels_ids.find(id =>
          id.includes('floating_gmf'),
        )
        this.$store.dispatch(SNK('sol_resetCustomSol', this.gamestateNamespace), {
          item,
          channelId,
          routerParams: {
            push: true,
            replace: false,
            saveLocally: true,
          },
        })
        return
      }

      const tableRows = this.$store.getters[SNK('gmfs_tableRows', this.gamestateNamespace)]
      const idx = tableRows.findIndex(i => i.active)
      let nextIdx = idx + num
      const l = tableRows.length - 1
      nextIdx = nextIdx > l ? 0 : nextIdx < 0 ? l : nextIdx
      const res = tableRows[nextIdx]
      const routerParams = {
        push: true,
        replace: false,
        saveLocally: true,
      }
      this.$store.dispatch('globals_action', 'disable_history_watcher')
      setTimeout(() => {
        const channelId = this.$store.getters.bc_channels_ids.find(id =>
          id.includes('floating_gmf'),
        )
        this.$store.dispatch(SNK('gmfs_set_tableRow', this.gamestateNamespace), {
          item: res,
          instaApply: true,
          routerParams,
          keepHistoryTree: true,
          channelId,
        })
        this.pr_dispatchMessage({ id: 'gmf_change' })
      })
    }, 222),
    hSpotsJumpNext(num) {
      const hSpot = this.$store.getters[SNK('history_spot', this.gamestateNamespace)]
      if (!hSpot) return
      let jumpId = hSpot.id + num
      const hSpots = this.$store.getters[SNK('history_spots', this.gamestateNamespace)]
      const ll = hSpots.length - 1
      jumpId = jumpId < 0 ? ll : jumpId > ll ? 0 : jumpId
      if (!hSpots.some(i => i.id === jumpId)) return
      this.$store.dispatch('globals_action', `hsjump_${jumpId}`)
    },
  },
  computed: {
    gamestateNamespace() {
      return this.$store.getters.user_has_flag('CR_REPORTS')
        ? this.soltab === 'reports'
          ? namespaces.REPORTS
          : namespaces.STUDY
        : ''
    },
    commonProps() {
      return {
        mobileLandscape: this.mobile && this.$store.getters.screen_landscape,
        max1280: this.$store.getters.screen_max1280,
        useHex: this.$store.getters.sw_actions_grouping === 'swv_grouping_none',
        grouping: this.$store.getters.sw_actions_grouping,
        showBetsIn: this.$store.getters.show_bets_in,
        showEvIn: this.$store.getters.show_ev_in,
        mobile: this.mobile,
        fullscreen: this.$store.getters[`sw_study_${this.$store.getters.sw_tabs_study}_fullscreen`],
      }
    },

    // ----------
    soltab() {
      return this.$store.getters.sw_tabs_study
    },
    loadingCustomSolution() {
      return this.$store.getters.sw_tabs_study
    },
    isDev() {
      return import.meta.env.VITE_ENV !== 'production'
    },
    street() {
      return this.$store.getters.street
    },
    glo() {
      return this.$store.state.globals
    },
    hotkeys() {
      return this.$store.getters.hotkeys
    },
    hotkeysPressedBottom() {
      return this.$store.getters.hotkeys_pressed_bottom
    },
    mobile() {
      return this.$store.getters.globals_mobile
    },

    usingCustomSol() {
      return this.$store.getters.sol_elite.want_use
    },
    panelReplay() {
      return this.$store.getters.panel_replay
    },
  },
  watch: {
    hotkeysPressedBottom(keys) {
      if (this.panelReplay) return
      if (!keys) return
      const hotkeyActions = {
        card_up: {
          keys: ['shift', 'arrowup'],
          fn: () => this.getNextBoardCard('up'),
          hasPreventDefault: true,
        },
        card_down: {
          keys: ['shift', 'arrowdown'],
          fn: () => this.getNextBoardCard('down'),
          hasPreventDefault: true,
        },
        card_left: {
          keys: ['shift', 'arrowleft'],
          fn: () => this.getNextBoardCard('left'),
          hasPreventDefault: true,
        },
        card_right: {
          keys: ['shift', 'arrowright'],
          fn: () => this.getNextBoardCard('right'),
          hasPreventDefault: true,
        },
        hSpots_jump_left: {
          keys: ['arrowleft'],
          fn: () => this.hSpotsJumpNext(-1),
          hasPreventDefault: true,
        },
        hSpots_jump_right: {
          keys: ['arrowright'],
          fn: () => this.hSpotsJumpNext(1),
          hasPreventDefault: true,
        },
        nextFormat: {
          keys: ['arrowdown'],
          fn: () => this.getNextGameformat(1),
          hasPreventDefault: true,
        },
        prevFormat: {
          keys: ['arrowup'],
          fn: () => this.getNextGameformat(-1),
          hasPreventDefault: true,
        },
      }
      this.$store.dispatch('hotkeys_perform_action', { keys, hotkeyActions })
    },
  },
}
</script>
<style lang="scss" scoped>
#solution-browser {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.solbrowser {
  &_tabs {
    margin-top: 0.5rem;
    display: flex;
    gap: 1rem;
    min-width: 0;
    &_pills {
      margin: 0 0 auto auto;
    }
  }
}
</style>
